var _ = require('underscore');
var moment = require('moment');
var TorsoView = require('backbone-torso/modules/View');
var Events = require('backbone-torso/modules/Events');
var specificAppointmentFeatureConfigCollection = require('./../../appointment/specificAppointmentFeatureConfigCollection');
var userService = require('./../../user/userService');
var launcherAppInterface = require('./../../launcherApplicationInterface');
var onsiteConfigModel = require('./../../onsite-config/onsiteConfigModel');
var appointmentConfigurationModel = require('./../../appointment-config/appointmentConfigurationModel');
var secureMessageAppointmentTemplate = require('./secureMessageAppointmentTemplate.hbs');
var applicationUtils = require('./../../applicationUtils');
var AppointmentTypeParentBehavior = require('./../../behaviors/AppointmentTypeParentBehavior');
var AppointmentTypeDataBehavior = require('./../../appointment-type/AppointmentTypeDataBehavior');
var appointmentExtService = require('./../../urls').appointmentExtService;
var MultiAppointmentSelectionBehavior = require('./../../appointment/MultiAppointmentSelectionBehavior');
var wizardUtils = require('./../../wizard/wizardUtils');

var PRIMARY_ATTRIBUTE = 'primary';
var SET_PRIMARY_EVENT = 'SecureMessageAppointmentView:' + PRIMARY_ATTRIBUTE;

/**
 * @class SecureMessageAppointmentView
 * @constructor
 * @param option {Object}
 *   @param options.model {Torso.Model}
 *   @param options.context.userIsStaff {boolean}
 *   @param options.context.patientVecnaId {String}
 */
module.exports = TorsoView.extend({
  tagName: 'li',
  className: 'brec-list__item brec-list__item--border brec-list__item--multi-linked',
  template: secureMessageAppointmentTemplate,
  _userIsStaff: false,

  /**
   * Adds attributes to the the top level element (this is to support existing behavioral tests that query these values).
   * This only works because, as an item view, this view will only be created when the model already exists.  Values will not
   * be updated dynamically if they change for this view after it renders.
   * @returns {{data-test-appointment-external-id: String, data-test-item-appointment: String}} the id and selector for appointment
   */
  attributes: function() {
    return {
      'data-test-appointment-external-id': this._getAppointmentExternalId(),
      'data-test-item-appointment': true,
      'data-test-vecna-internal-appointment': this.model.get('appointmentDTO.externalAppointment.vecnaInternalAppointment')
    };
  },

  behaviors: {
    appointmentTypeParentBehavior: AppointmentTypeParentBehavior,
    appointmentType: {
      behavior: AppointmentTypeDataBehavior,
      returnSingleResult: true,
      ids: { property: 'model:appointmentDTO.externalAppointment.type'}
    },
    parentAppointmentType: {
      behavior: AppointmentTypeDataBehavior,
      returnSingleResult: true,
      ids: { property: 'behaviors.appointmentType.data:parentExternalId'}
    },
    grandparentAppointmentType: {
      behavior: AppointmentTypeDataBehavior,
      returnSingleResult: true,
      ids: { property: 'behaviors.parentAppointmentType.data:parentExternalId'}
    },
    multiAppointmentSelectionBehavior: MultiAppointmentSelectionBehavior
  },

 prepareFields: {
   removeAppointmentTimeDisplay: function() {
     if (appointmentConfigurationModel.isLoading() || !appointmentConfigurationModel.hasLoadedOnce()) {
       return false;
     }
     return appointmentConfigurationModel.get('removeAppointmentTimeDisplay');
   }
 },

  /**
   * @method initialize
   * @param options {Object}
   *   @param options.context {Object} context for creating this view.
   *     @param options.context.userIsStaff {Boolean} true if the user is a staff user, false if a patient user.
   *     @param options.hideRegisterButton {Boolean} true to hide the register button, false to possibly display it based on other logic.
   * @override
   */
  initialize: function(options) {
    _.bindAll(this, '_setAppointmentConfigsToView');
    options = options || {};
    var context = options.context || {};
    this._userIsStaff = context.userIsStaff || false;
    this._hideRegisterButton = context.hideRegisterButton || false;
    var appointment = this.model.get('appointmentDTO').externalAppointment;
    this._clickable = !launcherAppInterface.isAtHospital() && !appointment.cancelled;
    this._patientVecnaId = this.model.get('appointmentDTO').patientVecnaId;

    this.listenTo(onsiteConfigModel, 'load-complete', this.render);
    onsiteConfigModel.loadOnce();
    this.listenTo(appointmentConfigurationModel, 'load-complete', this.render);
    appointmentConfigurationModel.loadOnce();

    this.listenTo(this.viewState, 'change:appointmentStatus change:preventBarcode', this.render);
    specificAppointmentFeatureConfigCollection.fetchByCriteria(this.model.get('appointmentDTO'))
      .done(this._setAppointmentConfigsToView);
  },

  /**
   * @method prepare
   * @override
   */
  prepare: function() {
    var context = TorsoView.prototype.prepare.apply(this);
    var appointment = this.model.get('appointmentDTO').externalAppointment;
    var appointmentTime = this.model.get('appointmentDTO').dateTimeString;
    var patientUsername = userService.currentDemographicsModel.get('messagingUsername');
    var appointmentExternalId = appointment.externalId;
    var appointmentTypeModel = this.getBehavior('appointmentType').data.getModel();

    context.appointment = appointment;
    context.appointmentTime = appointmentTime;
    context.primary = this.get(PRIMARY_ATTRIBUTE);
    context.patientVecnaId = this._patientVecnaId;
    context.isStatusUnread = this.model._isMessageUnread(patientUsername);
    context.isAtHospital = launcherAppInterface.isAtHospital();
    if (appointmentTypeModel) {
      context.appointmentTimeReplacement = appointmentTypeModel.getEffectiveAppointmentTimeReplacement();
    }
    context.clickable = this._clickable;
    if (onsiteConfigModel && onsiteConfigModel.get('barcodeEnabled') && appointment.preregistered && this.get('preventBarcode') === false) {
      context.barcodeUrl = appointmentExtService + '/' + appointmentExternalId + '/barcode';
    }
    context.currentApplication = applicationUtils.currentApplication;
    context.showRegisterButton = this._showRegisterButton();
    // Force the use of the single appt url by not setting the regDate.
    context.staffOrPatientRegUrl = wizardUtils.generateRootRegUrl({
      patientVecnaId: this._patientVecnaId,
      appointmentExternalId: this._getAppointmentExternalId(),
      userIsStaff: this._userIsStaff
    });
    return context;
  },

  /**
   * Set this view as the 'primary' view (the one that the patient is likely to want to check in now).
   */
  setPrimary: function() {
    this.set(PRIMARY_ATTRIBUTE, true);
    Events.trigger(SET_PRIMARY_EVENT, this);
  },

  /**
   * Set this view as not the 'primary' view if this view wasn't the view that triggered the primary setting to begin with.
   */
  setNotPrimary: function(primaryView) {
    if (primaryView !== this) {
      this.set(PRIMARY_ATTRIBUTE, false);
    }
  },

  /**
   * @method _activate
   * @override
   */
  _activate: function() {
    this.listenTo(this.viewState, 'change:' + PRIMARY_ATTRIBUTE, this.render);
    this.listenTo(Events, SET_PRIMARY_EVENT, this.setNotPrimary);
  },

  /**
   * @method _deactivate
   * @override
   */
  _deactivate: function() {
    this.stopListening();
  },

  /**
   * @method _showRegisterButton
   * @returns {boolean} whether or not to show the register button
   * @private
   */
  _showRegisterButton: function() {
    if (this._hideRegisterButton) {
      return false;
    }
    var appointment = this.model.get('appointmentDTO.externalAppointment');
    var appointmentType = this.getBehavior('appointmentType').data.getModel();

    return wizardUtils.isAppointmentPreRegisterable(appointment, appointmentType);
  },

  /**
   * Sets details of the specific appointment config to view
   * @method _setAppointmentConfigsToView
   * @param appointmentConfig {Object} config of this specific appointment
   * @private
   */
  _setAppointmentConfigsToView: function(appointmentConfig) {
    this.set('appointmentStatus', appointmentConfig.get('appointmentStatus'));
    var preventBarcode = appointmentConfig.get('configLevels.NORMAL.preRegistration.preventBarcode');
    this.set('preventBarcode', preventBarcode);
  },

  /**
   * @return {String} the appointment's external id for the appointment that this view is displaying.
   * @private
   */
  _getAppointmentExternalId: function() {
    return this.model.get('appointmentDTO.externalAppointment.externalId');
  },

  /**
   * @return {moment.Moment} the moment representation of the date & time for the appointment that this view is displaying.
   * @private
   */
  _getAppointmentDate: function() {
    var appointmentTime = this.model.get('appointmentDTO.externalAppointment.time');
    if (appointmentTime) {
      return moment(appointmentTime);
    }
    return null;
  }
});
